/**
 * 提示词配置与人设相关的 UI 文案分区（官方 locale 命名空间 `prompt-tool`）。
 *
 * 约定（与 locales.ts 相同）：zh 是键类型事实源，en 用 Record<keyof typeof zh, string>
 * 强制键集一致；只放用户可见文案，provider/model id、路径、用户内容、协议 code 不进字典。
 *
 * 键前缀分区：`configs.`（列表与编辑器）、`card.`（配置卡）、`form.`（表单字段）、
 * `field.`（通用控件）、枚举显示标签（`layer.` / `strategy.` / …）、`strategyParam.`
 * （按内容策略拆解的参数）、`variables.`（模板变量）、`templates.`（模板浮层）、
 * `persona.`（人设卡）。枚举标签由 prompt-config-policy.ts 的值 → 键映射查表，
 * 未知值回退原始值，不误译。
 */
export const PROMPTS_ZH = {
  'variables.deleteTitle': '清空模板变量？',
  'variables.deleteDescription': '清空当前模块的全部模板变量；提示词配置仍保留。',
  'variables.deleteFailed': '模板变量清空失败，请重试。',
  'card.actionsAria': '{name} 的操作',
  'card.deleteTitle': '删除 {name}',
  'card.deleteDescription': '从当前模块移除“{name}”及其配置。',
  'card.reloadTitle': '重新读取 {name}',
  'card.reloadDescription': '重新读取将丢弃“{name}”的未保存草稿，并采用当前磁盘内容。',
  'card.operationFailed': '操作失败，请重试',
  'card.fileConflictDetail': '磁盘版本已变化，本地草稿保留。',
  'card.fileMissing': '文件不存在',
  'card.fileTooLarge': '文件超过读取上限',
  'card.chip.audience': '受众：{value}',
  'card.unsaved': '有未保存的修改',
  'field.number.invalid': '请输入有效数字',
  'field.number.integer': '请输入有效整数',
  'customTools.invalidDraft': '请先修正工具的 JSON 或数字输入，再保存。',
  'persona.removeDescription': '移除当前模块的人设，并丢弃此处未保存的修改。',
  'presetSwitcher.delete.description': '删除模块“{name}”的用户目录副本，无法撤销。指令文件正文不受影响。',
  'characters.delete.description': '从角色库永久删除“{name}”。已应用到模块的内容不会一并移除。',
  'configs.notice.savedSnapshot': '已保存本次配置快照；后续修改继续保留。',
  'configs.notice.saveIncomplete': '部分配置或指令文件尚未保存，请查看错误后重试。',
  'configs.notice.valid': '配置校验通过',
  'configs.clearFilters': '清除筛选',
  'configs.showAll': '显示全部',
  'configs.createdHidden': '新建对象被当前筛选隐藏。',
  'configs.searchSorting': '搜索期间暂停排序；清除搜索后可调整同一插入点内顺序。',
  'configs.batch.enableVisible': '启用',
  'configs.batch.disableVisible': '停用',
  'configs.view.insertion': '插入点',
  'configs.view.strategy': '内容策略',
  'configs.createFirst': '创建第一条配置',
  'configs.chooseEditable': '选择或新建可编辑模块',
  'configs.readOnly.disabled': '模块写入已关闭，请在模块配置中开启。',
  'configs.readOnly.system': '当前模块只读，请选择可编辑模块。',
  'workspace.refreshing': '正在刷新，保留当前数据',
  'workspace.retry': '重新读取',
  // 配置列表与编辑器（PromptConfigList / PromptConfigsEditor）。
  'configs.heading.all': '模块列表',
  'configs.heading.layer': '本层配置',
  'configs.meta': '{total} 条配置 · {enabled} 条启用；上下移动控制同层顺序。',
  'configs.validate': '校验',
  'configs.validating': '校验中…',
  'configs.save': '保存配置',
  'configs.saving': '保存中…',
  'configs.filter.aria': '过滤提示词配置',
  'configs.filter.placeholder': '过滤提示词配置：按标识 / 名称…',
  'configs.view.aria': '按层级或策略过滤',
  'configs.view.all': '全部',
  'configs.view.worldBook': '世界书',
  'worldBookDiag.title': '世界书诊断（最近一次注入）',
  'worldBookDiag.hint': '只读观测：候选、入选与真实提交的原因来自引擎实际求值，不改变筛选结果与时间窗。',
  'worldBookDiag.refresh': '刷新诊断',
  'worldBookDiag.empty': '本次会话还没有可显示的世界书记录（先发送一条命中世界书的消息）。',
  'worldBookDiag.truncated': '记录超出上限，仅展示前 200 条。',
  'worldBookDiag.failed': '读取诊断失败：{reason}',
  'worldBookDiag.stage.excluded': '未参与',
  'worldBookDiag.stage.rejected': '被拒',
  'worldBookDiag.stage.candidate': '候选',
  'worldBookDiag.stage.selected': '入选',
  'worldBookDiag.stage.committed': '已注入',
  'configs.view.layer': '层级：{layer}',
  'configs.batch.enable': '启用',
  'configs.batch.disable': '禁用',
  'configs.error.missingId': '(缺 id)',
  'configs.empty.subagent.title': '还没有子代理可见的配置',
  'configs.empty.subagent.desc': '从上方「新建」插入一条（插入后可在卡片「消息受众」下拉自由切换仅主会话/公用/仅子代理），或到主设置「配置」从目录导入。',
  'configs.empty.all.title': '还没有自定义配置',
  'configs.empty.all.desc': '从上方模板插入一条，或从本地目录导入；默认四条内置配置不受影响。',
  'configs.empty.layer.title': '「{layer}」层还没有内容',
  'configs.empty.layer.desc': '用顶部「添加注入模板」在本层新建配置；能力与组合可在本层设置区添加。不会自动创建空卡片。',
  'configs.noMatch': '没有匹配「{keyword}」的配置。',
  'configs.noMatch.modules': '没有匹配「{keyword}」的提示词配置；能力模块不受此搜索影响。',
  'configs.notice.validateFailed': '校验请求失败：{reason}',
  'configs.notice.saved': '已校验并保存（{count} 条提示词配置）',
  'configs.notice.saveFailed': '保存失败：{reason}',
  'configs.notice.copied': '已复制',
  'configs.notice.deleted': '已删除',
  'configs.editor.aria': '主会话模块列表',

  // 配置卡（PromptConfigCard）。
  'card.dragHint': '拖动调整顺序',
  'card.enableHint': '点击启用',
  'card.disableHint': '点击关闭',
  'card.enableAria': '启用 {name}',
  'card.moveUp': '上移',
  'card.moveDown': '下移',
  'card.duplicate': '复制',
  'card.delete': '删除',
  'card.confirmDelete': '确认删除',
  'card.cancel': '取消',
  'card.chip.position': '位置：{value}',
  'card.chip.merged': '合并发送',
  'card.chip.order': '顺序：{order}',
  'card.chip.group': '分组：{name}',
  'card.chip.exclusiveGroup': '互斥组：{name}',
  'card.chip.fileDirty': '未保存',
  'card.chip.fileSaving': '保存中',
  'card.chip.fileConflict': '磁盘已变，需重新读取',
  'card.chip.fileUnavailable': '文件不可读',
  'card.fileStatusDetail': '指令文件状态：{message}',
  'card.saveFile': '保存到文件',
  'card.reloadFile': '重新读取',

  // 表单字段（PromptConfigForm）。
  'form.section.basic': '基础信息',
  'form.navigation.label': '编辑配置',
  'form.navigation.conditions': '条件',
  'form.navigation.execution': '执行',
  'form.navigation.settings': '设置',
  'form.navigation.invalid': '有未修正的输入错误',
  'form.section.rules': '注入规则',
  'form.section.scope': '作用范围',
  'form.section.content': '内容',
  'form.section.strategy': '本条规则的行为',
  'form.text.stream': '替代模型输出',
  'form.text.toolResult': '工具结果或拦截反馈',
  'form.text.mainSession': '注入主会话的文本',
  'form.text.actionHint': '仅在上方选择的行为生效时使用此文本。',
  'form.layerSettings.label': '本层共享设置（{layer}）',
  'form.layerSettings.hint': '作用于当前模块的「{layer}」相关能力，同层所有规则共用。修改这里会影响其他卡片；其他视图仅编辑当前规则。',
  'form.advanced.label': '高级元数据',
  'form.advanced.setCount': '高级元数据 · 已设置 {count} 项',
  'form.id.label': '标识',
  'form.id.hint': '配置的唯一标识，不能为空',
  'form.name.label': '名称',
  'form.name.hint': '模块列表中显示的名称',
  'form.layer.label': '注入层',
  'form.layer.hint': '决定提示内容注入的位置',
  'form.strategy.label': '内容策略',
  'form.strategy.hint': '决定提示内容的生成方式',
  'form.kind.label': '配置类型',
  'form.kind.hint': '顺序配置按顺序值排列；固定锚点优先',
  'form.role.label': '消息角色',
  'form.role.hint': '选择注入消息使用的角色；消息批只接受「用户」',
  'form.position.label': '拼接位置',
  'form.position.hint': '决定内容在同层消息中的位置',
  'form.merge.label': '合并方式',
  'form.merge.hint': '决定同位置内容是否合并发送',
  'form.order.label': '顺序',
  'form.order.hint': '数值越小越靠前',
  'form.order.insert': '插入到官方位置…',
  'form.order.insertLast': '全部官方位置之后',
  'form.order.layerOnly': '同层同位置的排列通过拖拽或上下移保存；不改变其他插入点。',
  'form.order.worldBookPriority': '世界书选择优先级',
  'form.order.worldBookHint': '预算不足时较大值优先选择；注入排列仍由配置顺序决定。',
  'orderGroup.identity': '身份与人设前',
  'orderGroup.policy': '策略与引用',
  'orderGroup.tools': '工具指导',
  'orderGroup.sdk': 'SDK 与外部面',
  'orderGroup.deliverable': '产出与结构化',
  'orderGroup.closing': '人设后收尾',
  'orderGroup.runtimePolicy': '运行策略',
  'form.group.label': '互斥组',
  'form.group.hint': '同组启用互斥后，只执行排序最前的启用配置',
  'form.exclusive.label': '互斥',
  'form.exclusive.hint': '开启后，同一互斥组只执行排序最前的启用配置',
  'form.dedupe.label': '去重方式',
  'form.dedupe.hint': '控制配置的重复执行范围',
  'form.promotion.label': '晋升范围',
  'form.promotion.hint': '决定配置是否要求会话晋升',
  'form.audience.label': '消息受众',
  'form.audience.hint': '缺省（通用）=主会话与子代理都注入',
  'form.modelScope.label': '模型范围',
  'form.modelScope.hint': '限制配置生效的模型类型',
  'form.text.label': '注入内容',
  'form.text.hint': '留空时不注入；支持使用 {{key}} 引用模板变量',
  'form.text.aria': '注入内容（空 = 不注入）',
  'form.text.fileReadOnly': '文件当前不可读或磁盘版本已变化：正文保持只读，重新读取后再编辑或保存。',
  'form.fill.label': '填充来源',
  'form.fill.hint': '选择动态内容的来源',
  'form.sourceKind.label': '来源类型',
  'form.sourceKind.hint': '设置注入消息的来源标记；默认使用配置标识',
  'form.form.label': '消息形式',
  'form.form.hint': '设置注入消息的呈现形式',
  'form.summary.label': '摘要',
  'form.summary.hint': '注入消息的简短说明',
  'form.templateFile.label': '模板文件',
  'form.templateFile.hint': '从模块目录读取内容模板',
  'form.identity.scope.label': '幂等范围',
  'form.identity.scope.hint': '决定重复配置的识别范围',
  'form.identity.value.label': '幂等值',
  'form.identity.value.hint': '留空时使用配置标识',
  'form.subject.label': '匹配对象',
  'form.subject.hint': '条件判定读取的文本；选「层缺省」时不写该字段，由所在层决定',
  'form.subject.defaultHint': '当前未声明匹配对象：本层缺省使用「{value}」。',
  'form.match.group': '条件判定',
  'form.match.hint': '匹配键按字面文本匹配（正则元字符自动转义）；要按正则匹配请写成 /pattern/flags，或开启「正则匹配」。至少填一个主键或副键才会生效；留空 = 无条件命中。',
  'form.match.keys.label': '主键',
  'form.match.keys.hint': '主键命中即参与组合逻辑判定',
  'form.match.keys.placeholder': '匹配键，回车添加',
  'form.match.secondaryKeys.label': '副键',
  'form.match.secondaryKeys.hint': '与主键共同参与组合逻辑判定',
  'form.match.secondaryKeys.placeholder': '副键，回车添加',
  'form.match.logic.label': '组合逻辑',
  'form.match.logic.hint': '主键与副键的组合判定方式',
  'form.match.caseSensitive.label': '区分大小写',
  'form.match.caseSensitive.hint': '开启后匹配区分大小写',
  'form.match.wholeWords.label': '整词匹配',
  'form.match.wholeWords.hint': '开启后键必须作为完整词出现',
  'form.match.useRegex.label': '键的匹配方式',
  'form.match.useRegex.hint': '键默认按字面文本匹配（正则元字符会被转义）；「自动」只在键写成 /pattern/flags 时按正则',
  'form.match.useRegex.auto': '自动（键为 /…/ 时按正则）',
  'form.match.useRegex.force': '强制正则',
  'form.match.useRegex.literal': '强制字面',

  // 通用字段控件（OptionField / JsonField）。
  'field.default': '（默认）',
  'field.current': '{value}（当前值）',
  'field.json.invalid': 'JSON 无效',
  'field.json.mustBeObject': '必须是 JSON 对象',
  'field.json.advanced': '高级参数（JSON）',

  // 注入层显示标签（PromptConfigList 筛选、配置卡 chips 与创建菜单）。
  'layer.pre-step': '前置步骤',
  'layer.system-section': '系统提示段',
  'layer.runtime-context': '运行上下文',
  'layer.agent-request': '代理请求',
  'layer.llm-stream': '模型流',
  'layer.tool-pipeline': '工具链',
  'layer.turn-stop': '轮次停止层',
  'layer.subagent-start': '子代理启动层',
  'layer.subagent-end': '子代理结束层',

  // 枚举显示标签（值 → 键映射在 prompt-config-policy.ts）。
  'audience.none': '公用（缺省）',
  'audience.main': '仅主会话',
  'audience.subagent': '仅子代理',
  // 条件判定的匹配对象与组合逻辑。
  'subject.layerDefault': '层缺省',
  'subject.toolArgs': '工具入参（toolArgs）',
  'subject.toolResult': '工具结果（toolResult）',
  'subject.userMessage': '用户消息（userMessage）',
  'subject.assistantText': '助手输出（assistantText）',
  'subject.subagentInfo': '子代理信息（subagentInfo）',
  'match.any': '任一主键命中（any）',
  'match.all': '主键命中且副键全命中（all）',
  'match.not': '主键命中且副键全未命中（not）',
  'match.notAny': '主键命中且至少一个副键未命中（notAny）',
  'strategy.static': '固定文本',
  'strategy.placeholder': '动态填充',
  'strategy.firstTurnAnchor': '首轮锚定',
  'strategy.guideAuto': '自动引导',
  'strategy.customFallback': '自定义回退',
  'strategy.worldBook': '世界书',
  'slotKind.ordered': '顺序配置',
  'slotKind.anchor': '固定锚点',
  'role.user': '用户',
  'position.afterUser': '用户消息后',
  'position.beforeAll': '全部消息前',
  'position.afterAll': '全部消息后',
  'merge.separate': '分开发送',
  'merge.merged': '合并发送',
  'dedupe.none': '不去重',
  'dedupe.session': '每会话一次',
  'dedupe.batch': '当前批次',
  'promotion.none': '不晋升',
  'promotion.main': '主会话',
  'promotion.subagents': '包含子代理',
  'modelScope.all': '全部模型',
  'modelScope.pro': '专业模型',
  'modelScope.flash': '快速模型',
  'fill.none': '未选择',
  'fill.instructionHint': '指令提示',
  'fill.envFacts': '环境信息',
  'fill.skillCatalog': '技能目录',
  'sourceKind.default': '默认',
  'sourceKind.plugin': '插件',
  'sourceKind.instructionHint': '指令提示',
  'sourceKind.skillCatalog': '技能目录',
  'sourceKind.envFacts': '环境信息',
  'sourceKind.instructionFile': '指令文件正文',
  'sourceForm.notice': '通知',
  'sourceForm.hint': '提示',
  'sourceForm.instructions': '指令正文',
  'sourceForm.default': '默认',
  'identity.plugin': '按插件',
  'identity.kind': '按注入类型',
  'emptyBehavior.skip': '跳过',
  'emptyBehavior.text': '使用提示文本',
  'selectiveLogic.0': '0 · 任一命中（默认）',
  'selectiveLogic.1': '1 · 副键全排除',
  'selectiveLogic.2': '2 · 部分排除',
  'selectiveLogic.3': '3 · 副键全包含',

  // 按内容策略拆解的参数（StrategyParamsFields）。
  'strategyParam.sectionName.label': '段名',
  'strategyParam.sectionName.hint': '留空时使用配置标识；同名段会覆盖已有段',
  'strategyParam.complete.label': '独占',
  'strategyParam.complete.hint': '开启后本段成为唯一的系统提示段：身份注入、人设后缀、工具指南等其余系统段全部被抑制（工具链、运行时上下文与模板变量仍保留）。同一模块只能启用一个，且与顶层人设的「独占」互斥。',
  'strategyParam.suppressRuntimeContext.label': '动态抑制',
  'strategyParam.suppressRuntimeContext.hint': '开启后不附加运行时上下文',
  'strategyParam.contextName.label': '注册名',
  'strategyParam.contextName.hint': '注册到运行上下文的段名；留空时使用配置标识',
  'strategyParam.patch.label': '请求配置补丁',
  'strategyParam.patch.hint': '浅合并进本次调用的 LlmCallConfig（JSON 对象）；空对象等于不改动',
  'strategyParam.replace.label': '整体替换',
  'strategyParam.replace.hint': '开启后丢弃基线调用配置，只用上方补丁',
  'strategyParam.mode.label': '模型流模式',
  'strategyParam.mode.hint': '透传时不改动模型流；替换时用本配置正文替代整个模型流',
  'strategyParam.mode.pass': '透传',
  'strategyParam.mode.replace': '替换',
  'strategyParam.toolNames.label': '生效工具',
  'strategyParam.toolNames.hint': '工具名逗号分隔；留空等于匹配所有已注册工具，做定向门时不要留空',
  'strategyParam.preDecision.label': '执行前裁决',
  'strategyParam.preDecision.hint': '命中条件的工具调用在 pre-execute 如何裁决',
  'strategyParam.preDecision.allow': '放行',
  'strategyParam.preDecision.deny': '拒绝',
  'strategyParam.preDecision.ask': '询问',
  'strategyParam.denyReason.label': '拒绝理由',
  'strategyParam.denyReason.hint': '拒绝时回给模型的原因；留空使用引擎默认文案',
  'strategyParam.postAction.label': '执行后裁决',
  'strategyParam.postAction.hint': '命中条件的工具结果如何处理',
  'strategyParam.postAction.accept': '接受',
  'strategyParam.postAction.replace': '替换',
  'strategyParam.postAction.block': '阻断',
  'strategyParam.turnStopNote': '本层没有可写实例参数：续跑上限由引擎固定，不开放配置。',
  'strategyParam.subagentEndNote': '结束事件本身只通知结果。可选择仅记录，或向所属主会话投递上下文；不改写子代理结果，也不唤醒空闲主会话。',
  'strategyParam.endAction.label': '结束后的行为',
  'strategyParam.endAction.hint': '向主会话注入时，文本在该主会话下一次处理输入时生效；同一次子代理运行只投递一次。',
  'strategyParam.endAction.observe': '仅记录结束',
  'strategyParam.endAction.injectMain': '向主会话注入文本',
  'strategyParam.request.hint': '本条规则修改模型调用配置，留空字段继承原请求。正文、工具列表与消息历史不属于此接口。',
  'strategyParam.request.provider': '提供商标识',
  'strategyParam.request.model': '模型标识',
  'strategyParam.request.reasoningEffort': '思维程度',
  'strategyParam.request.temperature': '采样温度',
  'strategyParam.request.maxTokens': '最大输出 Token',
  'strategyParam.request.stop': '停止序列',
  'strategyParam.request.stopHint': '每行一条，保留序列内的空格；留空时继承原请求。',
  'strategyParam.anchorManaged': '以下字段在当前产物中来自模块参数，这里只读回显；来源参数位于「本层引擎设置」。显式局部配置仍保留更高覆盖优先级，重建后按实际来源刷新。',
  'strategyParam.managed.local': '当前产物使用此配置的局部覆盖；下方字段可在此编辑，未声明的字段使用配置缺省值。',
  'strategyParam.managed.source': '来源参数：{param}',
  'strategyParam.managed.derived': '计算结果',
  'strategyParam.managed.empty': '空（引擎缺省）',
  'strategyParam.managed.followsCustom': '随自定义引导开关切换模型范围',
  'strategyParam.guideManaged': '以下字段在当前产物中来自模块参数，这里只读回显；来源参数位于「本层引擎设置」。显式局部配置仍保留更高覆盖优先级，重建后按实际来源刷新。',
  'strategyParam.custom.label': '自定义',
  'strategyParam.custom.hint': '开启后固定使用自定义文本；关闭后按任务自动选择',
  'strategyParam.anchorText.label': '自定义锚文本',
  'strategyParam.anchorText.hint': '启用自定义时固定注入此文本',
  'strategyParam.buildPattern.label': '构建任务规则',
  'strategyParam.buildPattern.hint': '命中后使用构建引导',
  'strategyParam.complexPattern.label': '复杂任务规则',
  'strategyParam.complexPattern.hint': '命中后使用深度引导',
  'strategyParam.firstTurnBuild': '构建引导',
  'strategyParam.firstTurnInspect': '排查引导',
  'strategyParam.deepGuide': '深度引导',
  'strategyParam.guideText.label': '自定义引导',
  'strategyParam.guideText.hint': '启用自定义时固定注入此文本',
  'strategyParam.guideReuse': '复杂任务沿用锚定模块的复杂任务规则。',
  'strategyParam.guideWeak': '简短引导',
  'strategyParam.firstTurnWord.label': '触发词',
  'strategyParam.firstTurnWord.hint': '晋升后的首个推理内容命中此词时注入',
  'strategyParam.constant.label': '常驻',
  'strategyParam.constant.hint': '开启后每轮注入；关闭后仅关键词命中时注入',
  'strategyParam.keys.label': '主关键词',
  'strategyParam.keys.hint': '消息命中任一关键词时注入',
  'strategyParam.keys.placeholder': '关键字，回车添加',
  'strategyParam.secondaryKeys.label': '次关键词',
  'strategyParam.secondaryKeys.hint': '与主关键词共同参与触发判断',
  'strategyParam.secondaryKeys.placeholder': '次级关键字，回车添加',
  'strategyParam.caseSensitive.label': '区分大小写',
  'strategyParam.caseSensitive.hint': '开启后关键词必须匹配大小写',
  'strategyParam.wholeWords.label': '整词匹配',
  'strategyParam.wholeWords.hint': '开启后关键词必须作为完整词出现',
  'strategyParam.useRegex.label': '正则匹配',
  'strategyParam.useRegex.hint': '开启后主次关键词按正则表达式匹配',
  'strategyParam.selectiveLogic.label': '触发逻辑',
  'strategyParam.selectiveLogic.hint': '控制主关键词与次关键词的组合判断方式',
  'strategyParam.fillText.label': '自定义提示',
  'strategyParam.fillText.hint': '覆盖文件和动态探测内容；留空时使用默认值',
  'strategyParam.envKeys.label': '环境变量',
  'strategyParam.envKeys.hint': '允许读取的环境变量；逗号分隔，留空时使用默认值',
  'strategyParam.limit.label': '数量上限',
  'strategyParam.limit.hint': '填写正整数；留空时不限制',
  'strategyParam.fields.label': '返回字段',
  'strategyParam.fields.hint': '技能目录返回的字段；逗号分隔',
  'strategyParam.providers.label': '来源白名单',
  'strategyParam.providers.hint': '允许使用的技能来源；逗号分隔，留空时允许全部',
  'strategyParam.emptyBehavior.label': '空结果处理',
  'strategyParam.emptyBehavior.hint': '无内容时选择跳过或注入提示文本',
  'strategyParam.emptyText': '空结果提示',
  'strategyParam.noParams': '本策略无额外参数；模板变量可在上方编辑。',

  // 模板变量编辑器与模块级变量卡（VariablesEditor / PromptConfigsEditor）。
  'variables.title': '模板变量',
  'variables.cardMeta': '模块级 {{key}} 插值 · {count} 个变量',
  'variables.toggleDisable': '点击停用模板变量插值',
  'variables.toggleEnable': '点击启用模板变量插值',
  'variables.enableAria': '启用模板变量插值',
  'variables.confirmClear': '确认清空',
  'variables.cancel': '取消',
  'variables.delete': '删除',
  'variables.disabledHint': '模板变量插值已停用：{{key}} 将不再被替换（模块级变量文件不生成）。',
  'variables.add': '添加',
  'variables.empty': '暂无变量；可使用 {{key}} 引用。',
  'variables.nameAria': '模板变量名',
  'variables.valueAria': '模板变量值',
  'variables.namePlaceholder': '变量名',
  'variables.valuePlaceholder': '变量值',
  'variables.removeAria': '删除变量 {name}',

  // 内置模板浮层（TemplatePicker）。
  'templates.aria': '选择内置模板',
  'templates.title': '选择内置模板（{layer}）',
  'templates.closeAria': '关闭模板选择',
  'templates.group.variables': '变量',
  'templates.variables.hint': '模板变量（模块级 {{key}} 插值）',
  'templates.empty': '本层暂无内置模板。',
  'templates.group.tools': '工具',
  'templates.loadFailed': '读取模板库失败：{reason}',
  'templates.emptyList': '读取模板库失败：模板列表为空',
  'templates.duplicateId': 'id 已存在：{id}（请先改名或删除同 id 配置）',
  'templates.inserted': '已插入模板 {file}（id={id}）',
  'templates.layer.pre-step': '消息批层',
  'templates.layer.system-section': '系统段层',
  'templates.layer.runtime-context': '运行上下文',
  'templates.layer.agent-request': '调用配置层',
  'templates.layer.llm-stream': '模型流层',
  'templates.layer.tool-pipeline': '工具管线层',
  'templates.layer.turn-stop': '轮次停止层',
  'templates.layer.subagent-start': '子代理启动层',
  'templates.layer.subagent-end': '子代理结束层',

  // 人设卡（PresetPersonaCard）。
  'persona.name': '人设',
  'persona.meta.declared': 'module.yml 顶层 persona · 官方 @deepseek-ai/dsh-persona 行同构',
  'persona.meta.inherited': '未声明：继承宿主部署人设',
  'persona.prefix.label': '前缀 prefix',
  'persona.prefix.aria': '人设前缀',
  'persona.prefix.hint': '渲染为 deployment:persona-prefix（第一方指导之前，官方 order 0）；支持 {{model}} / {{cwd}} 等官方变量；留空 = 该段渲染时丢弃。',
  'persona.suffix.label': '后缀 suffix',
  'persona.suffix.aria': '人设后缀',
  'persona.suffix.hint': '渲染为 deployment:persona-suffix（第一方指导之后，官方 order 10200）；留空 = 遮蔽宿主后缀。',
  'persona.complete.label': '独占',
  'persona.complete.hint': 'prefix 成为唯一系统提示段；与提示词配置的「独占」互斥',
  'persona.runtimeContext.label': '动态运行时上下文',
  'persona.runtimeContext.hint': '关闭 = 该 scope 不附加动态快照（官方 includeRuntimeContext:false）',
  'persona.saving': '保存中…',
  'persona.save': '保存人设',
  'persona.remove': '移除人设段',
  'persona.notice.loadFailed': '人设读取失败',
  'persona.notice.saved': '人设已保存并重建',
  'persona.notice.removed': '已移除人设段（回落宿主部署人设）',
  'persona.notice.saveFailed': '人设保存失败',
} as const

export const PROMPTS_EN: Record<keyof typeof PROMPTS_ZH, string> = {
  'variables.deleteTitle': 'Clear template variables?',
  'variables.deleteDescription': 'Clear all template variables in this module. Prompt configurations are retained.',
  'variables.deleteFailed': 'Could not clear template variables. Try again.',
  'card.actionsAria': 'Actions for {name}',
  'card.deleteTitle': 'Delete {name}',
  'card.deleteDescription': 'Remove “{name}” and its configuration from this module.',
  'card.reloadTitle': 'Reload {name}',
  'card.reloadDescription': 'Reload discards the unsaved draft of “{name}” and uses the current disk content.',
  'card.operationFailed': 'Operation failed. Try again.',
  'card.fileConflictDetail': 'The disk version changed. Your local draft is preserved.',
  'card.fileMissing': 'File missing',
  'card.fileTooLarge': 'File exceeds the read limit',
  'card.chip.audience': 'Audience: {value}',
  'card.unsaved': 'Unsaved changes',
  'field.number.invalid': 'Enter a valid number',
  'field.number.integer': 'Enter a valid integer',
  'customTools.invalidDraft': 'Correct the JSON or numeric tool input before saving.',
  'persona.removeDescription': 'Remove the persona from this module and discard its unsaved edits.',
  'presetSwitcher.delete.description': 'Permanently delete the user copy of module “{name}”. Instruction files are unaffected.',
  'characters.delete.description': 'Permanently delete “{name}” from the character library. Content already applied to modules is retained.',
  'configs.notice.savedSnapshot': 'This configuration snapshot was saved. Later edits remain in your draft.',
  'configs.notice.saveIncomplete': 'Some configurations or instruction files were not saved. Review the errors and retry.',
  'configs.notice.valid': 'Configuration validation passed',
  'configs.clearFilters': 'Clear filters',
  'configs.showAll': 'Show all',
  'configs.createdHidden': 'The new item is hidden by the current filters.',
  'configs.searchSorting': 'Sorting is paused during search. Clear the search to reorder within an insertion point.',
  'configs.batch.enableVisible': 'Enable',
  'configs.batch.disableVisible': 'Disable',
  'configs.view.insertion': 'Insertion point',
  'configs.view.strategy': 'Content strategy',
  'configs.createFirst': 'Create your first configuration',
  'configs.chooseEditable': 'Select or create an editable module',
  'configs.readOnly.disabled': 'Module writing is off. Enable it in the module settings.',
  'configs.readOnly.system': 'This module is read-only. Select an editable module.',
  'workspace.refreshing': 'Refreshing; current data is retained',
  'workspace.retry': 'Reload',
  'configs.heading.all': 'Module list',
  'configs.heading.layer': 'Layer configs',
  'configs.meta': '{total} configs · {enabled} enabled; move up/down to reorder within the layer.',
  'configs.validate': 'Validate',
  'configs.validating': 'Validating…',
  'configs.save': 'Save configurations',
  'configs.saving': 'Saving…',
  'configs.filter.aria': 'Filter prompt configs',
  'configs.filter.placeholder': 'Filter prompt configs by id / name…',
  'configs.view.aria': 'Filter by layer or strategy',
  'configs.view.all': 'All',
  'configs.view.worldBook': 'World book',
  'worldBookDiag.title': 'World book diagnostics (last injection)',
  'worldBookDiag.hint': 'Read-only: candidate, selected and committed reasons come from the engine itself and never change the selection or timers.',
  'worldBookDiag.refresh': 'Refresh diagnostics',
  'worldBookDiag.empty': 'No world book records for this session yet (send a message that hits a world book entry first).',
  'worldBookDiag.truncated': 'Records exceed the limit; showing the first 200.',
  'worldBookDiag.failed': 'Failed to read diagnostics: {reason}',
  'worldBookDiag.stage.excluded': 'Not considered',
  'worldBookDiag.stage.rejected': 'Rejected',
  'worldBookDiag.stage.candidate': 'Candidate',
  'worldBookDiag.stage.selected': 'Selected',
  'worldBookDiag.stage.committed': 'Injected',
  'configs.view.layer': 'Layer: {layer}',
  'configs.batch.enable': 'Enable',
  'configs.batch.disable': 'Disable',
  'configs.error.missingId': '(missing id)',
  'configs.empty.subagent.title': 'No config is visible to subagents yet',
  'configs.empty.subagent.desc': 'Insert one with "New" above (then use the card\'s audience menu to switch between main-only / shared / subagent-only), or import a directory under "Configs" in the main settings.',
  'configs.empty.all.title': 'No custom configs yet',
  'configs.empty.all.desc': 'Insert one from a template above, or import from a local directory; the four built-in configs are unaffected.',
  'configs.empty.layer.title': 'No content in the "{layer}" layer yet',
  'configs.empty.layer.desc': 'Use "Add injection template" above to add a config here. Add capabilities and chained modules in this layer’s settings; empty cards are never created automatically.',
  'configs.noMatch': 'No config matches "{keyword}".',
  'configs.noMatch.modules': 'No prompt config matches "{keyword}"; capability modules are not affected by this search.',
  'configs.notice.validateFailed': 'Validation request failed: {reason}',
  'configs.notice.saved': 'Validated and saved ({count} prompt configs)',
  'configs.notice.saveFailed': 'Save failed: {reason}',
  'configs.notice.copied': 'Copied',
  'configs.notice.deleted': 'Deleted',
  'configs.editor.aria': 'Main-session module list',

  'card.dragHint': 'Drag to reorder',
  'card.enableHint': 'Click to enable',
  'card.disableHint': 'Click to disable',
  'card.enableAria': 'Enable {name}',
  'card.moveUp': 'Move up',
  'card.moveDown': 'Move down',
  'card.duplicate': 'Duplicate',
  'card.delete': 'Delete',
  'card.confirmDelete': 'Confirm delete',
  'card.cancel': 'Cancel',
  'card.chip.position': 'Position: {value}',
  'card.chip.merged': 'Merged send',
  'card.chip.order': 'Order: {order}',
  'card.chip.group': 'Group: {name}',
  'card.chip.exclusiveGroup': 'Exclusive group: {name}',
  'card.chip.fileDirty': 'Unsaved',
  'card.chip.fileSaving': 'Saving',
  'card.chip.fileConflict': 'Changed on disk — reload required',
  'card.chip.fileUnavailable': 'File unreadable',
  'card.fileStatusDetail': 'Instruction file status: {message}',
  'card.saveFile': 'Save to file',
  'card.reloadFile': 'Reload',

  'form.section.basic': 'Basics',
  'form.navigation.label': 'Edit configuration',
  'form.navigation.conditions': 'Conditions',
  'form.navigation.execution': 'Execution',
  'form.navigation.settings': 'Settings',
  'form.navigation.invalid': 'Unresolved input errors',
  'form.section.rules': 'Injection rules',
  'form.section.scope': 'Scope',
  'form.section.content': 'Content',
  'form.section.strategy': 'Rule behavior',
  'form.text.stream': 'Replacement model output',
  'form.text.toolResult': 'Tool result or blocking feedback',
  'form.text.mainSession': 'Text injected into the main session',
  'form.text.actionHint': 'This text is used only by the selected behavior above.',
  'form.layerSettings.label': 'Shared layer settings ({layer})',
  'form.layerSettings.hint': 'These settings control the current module’s capabilities associated with {layer} and are shared by all rules in the layer. Changes affect other cards; other views edit only this rule.',
  'form.advanced.label': 'Advanced metadata',
  'form.advanced.setCount': 'Advanced metadata · {count} set',
  'form.id.label': 'ID',
  'form.id.hint': 'Unique identifier of the config; must not be empty',
  'form.name.label': 'Name',
  'form.name.hint': 'Name shown in the module list',
  'form.layer.label': 'Injection layer',
  'form.layer.hint': 'Where the prompt content is injected',
  'form.strategy.label': 'Content strategy',
  'form.strategy.hint': 'How the prompt content is produced',
  'form.kind.label': 'Config kind',
  'form.kind.hint': 'Ordered configs sort by their order value; a fixed anchor takes precedence',
  'form.role.label': 'Message role',
  'form.role.hint': 'Role used by the injected message; the message batch only accepts User',
  'form.position.label': 'Insert position',
  'form.position.hint': 'Where the content sits among same-layer messages',
  'form.merge.label': 'Merge mode',
  'form.merge.hint': 'Whether same-position content is sent as one message',
  'form.order.label': 'Order',
  'form.order.hint': 'Smaller values come first',
  'form.order.insert': 'Insert at an official position…',
  'form.order.insertLast': 'After all official positions',
  'form.order.layerOnly': 'Drag or move cards to save their order within this layer and position; other insertion points stay independent.',
  'form.order.worldBookPriority': 'World book selection priority',
  'form.order.worldBookHint': 'Higher values are selected first when the budget is limited; card sequence still controls insertion order.',
  'orderGroup.identity': 'Before identity and persona',
  'orderGroup.policy': 'Policy and references',
  'orderGroup.tools': 'Tool guidance',
  'orderGroup.sdk': 'SDK and external surface',
  'orderGroup.deliverable': 'Deliverables and structured output',
  'orderGroup.closing': 'Persona closing',
  'orderGroup.runtimePolicy': 'Runtime policy',
  'form.group.label': 'Exclusive group',
  'form.group.hint': 'With exclusivity on, only the first enabled config of the group runs',
  'form.exclusive.label': 'Exclusive',
  'form.exclusive.hint': 'When on, only the first enabled config of the same exclusive group runs',
  'form.dedupe.label': 'Dedupe mode',
  'form.dedupe.hint': 'Scope in which the config may run only once',
  'form.promotion.label': 'Promotion scope',
  'form.promotion.hint': 'Whether the config requires the session to be promoted',
  'form.audience.label': 'Audience',
  'form.audience.hint': 'Unset (shared) = injected for both the main session and subagents',
  'form.modelScope.label': 'Model scope',
  'form.modelScope.hint': 'Restrict the config to a model class',
  'form.text.label': 'Injected content',
  'form.text.hint': 'Empty means no injection; {{key}} references a template variable',
  'form.text.aria': 'Injected content (empty = no injection)',
  'form.text.fileReadOnly': 'The file is unreadable or changed on disk: the body stays read-only until you reload it.',
  'form.fill.label': 'Fill source',
  'form.fill.hint': 'Source of the dynamic content',
  'form.sourceKind.label': 'Source kind',
  'form.sourceKind.hint': 'Source tag of the injected message; defaults to the config ID',
  'form.form.label': 'Message form',
  'form.form.hint': 'Presentation form of the injected message',
  'form.summary.label': 'Summary',
  'form.summary.hint': 'Short description of the injected message',
  'form.templateFile.label': 'Template file',
  'form.templateFile.hint': 'Read the content template from the module directory',
  'form.identity.scope.label': 'Idempotency scope',
  'form.identity.scope.hint': 'How duplicate configs are recognized',
  'form.identity.value.label': 'Idempotency value',
  'form.identity.value.hint': 'Defaults to the config ID when empty',
  'form.subject.label': 'Match subject',
  'form.subject.hint': 'Text the condition reads; "layer default" writes no subject and lets the layer decide',
  'form.subject.defaultHint': 'No subject is declared: this layer defaults to "{value}".',
  'form.match.group': 'Condition',
  'form.match.hint': 'Keys match literal text (regular-expression metacharacters are escaped); write /pattern/flags or turn on "Regular expressions" to match by regex. At least one primary or secondary key is required; empty means the condition always hits.',
  'form.match.keys.label': 'Primary keys',
  'form.match.keys.hint': 'A hit on any primary key enters the combination logic',
  'form.match.keys.placeholder': 'Type a key and press Enter',
  'form.match.secondaryKeys.label': 'Secondary keys',
  'form.match.secondaryKeys.hint': 'Combined with the primary keys by the combination logic',
  'form.match.secondaryKeys.placeholder': 'Type a secondary key and press Enter',
  'form.match.logic.label': 'Combination logic',
  'form.match.logic.hint': 'How primary and secondary keys combine',
  'form.match.caseSensitive.label': 'Case sensitive',
  'form.match.caseSensitive.hint': 'When on, matching is case sensitive',
  'form.match.wholeWords.label': 'Whole words',
  'form.match.wholeWords.hint': 'When on, keys must appear as whole words',
  'form.match.useRegex.label': 'Key matching',
  'form.match.useRegex.hint': 'Keys match literally by default (regex metacharacters are escaped); Auto treats /pattern/flags keys as regex',
  'form.match.useRegex.auto': 'Auto (/…/ keys as regex)',
  'form.match.useRegex.force': 'Force regex',
  'form.match.useRegex.literal': 'Force literal',

  'field.default': '(default)',
  'field.current': '{value} (current)',
  'field.json.invalid': 'Invalid JSON',
  'field.json.mustBeObject': 'Must be a JSON object',
  'field.json.advanced': 'Advanced parameters (JSON)',

  'layer.pre-step': 'Pre-step',
  'layer.system-section': 'System section',
  'layer.runtime-context': 'Runtime context',
  'layer.agent-request': 'Agent request',
  'layer.llm-stream': 'Model stream',
  'layer.tool-pipeline': 'Tool pipeline',
  'layer.turn-stop': 'Turn stop',
  'layer.subagent-start': 'Subagent start',
  'layer.subagent-end': 'Subagent end',

  'audience.none': 'Shared (default)',
  'audience.main': 'Main session only',
  'audience.subagent': 'Subagents only',
  'subject.layerDefault': 'Layer default',
  'subject.toolArgs': 'Tool arguments (toolArgs)',
  'subject.toolResult': 'Tool result (toolResult)',
  'subject.userMessage': 'User message (userMessage)',
  'subject.assistantText': 'Assistant text (assistantText)',
  'subject.subagentInfo': 'Subagent info (subagentInfo)',
  'match.any': 'Any primary key hits (any)',
  'match.all': 'Primary key plus all secondary keys hit (all)',
  'match.not': 'Primary key hits and no secondary key hits (not)',
  'match.notAny': 'Primary key hits and at least one secondary key misses (notAny)',
  'strategy.static': 'Static text',
  'strategy.placeholder': 'Dynamic fill',
  'strategy.firstTurnAnchor': 'First-turn anchor',
  'strategy.guideAuto': 'Automatic guide',
  'strategy.customFallback': 'Custom fallback',
  'strategy.worldBook': 'World book',
  'slotKind.ordered': 'Ordered config',
  'slotKind.anchor': 'Fixed anchor',
  'role.user': 'User',
  'position.afterUser': 'After the user message',
  'position.beforeAll': 'Before all messages',
  'position.afterAll': 'After all messages',
  'merge.separate': 'Send separately',
  'merge.merged': 'Merge into one message',
  'dedupe.none': 'No dedupe',
  'dedupe.session': 'Once per session',
  'dedupe.batch': 'Current batch',
  'promotion.none': 'No promotion',
  'promotion.main': 'Main session',
  'promotion.subagents': 'Include subagents',
  'modelScope.all': 'All models',
  'modelScope.pro': 'Pro models',
  'modelScope.flash': 'Flash models',
  'fill.none': 'Not selected',
  'fill.instructionHint': 'Instruction hint',
  'fill.envFacts': 'Environment facts',
  'fill.skillCatalog': 'Skill catalog',
  'sourceKind.default': 'Default',
  'sourceKind.plugin': 'Plugin',
  'sourceKind.instructionHint': 'Instruction hint',
  'sourceKind.skillCatalog': 'Skill catalog',
  'sourceKind.envFacts': 'Environment facts',
  'sourceKind.instructionFile': 'Instruction file',
  'sourceForm.notice': 'Notice',
  'sourceForm.hint': 'Hint',
  'sourceForm.instructions': 'Instructions',
  'sourceForm.default': 'Default',
  'identity.plugin': 'By plugin',
  'identity.kind': 'By injection kind',
  'emptyBehavior.skip': 'Skip',
  'emptyBehavior.text': 'Use the hint text',
  'selectiveLogic.0': '0 · Any key matches (default)',
  'selectiveLogic.1': '1 · No secondary key matches',
  'selectiveLogic.2': '2 · Partial exclusion',
  'selectiveLogic.3': '3 · All secondary keys match',

  'strategyParam.sectionName.label': 'Section name',
  'strategyParam.sectionName.hint': 'Defaults to the config ID; a same-name section replaces the existing one',
  'strategyParam.complete.label': 'Exclusive',
  'strategyParam.complete.hint': 'When on, this section becomes the only system prompt section: the harness identity, persona suffix, tool guidance and every other system section are suppressed (tools, runtime context and template variables are retained). One per module, and mutually exclusive with the top-level persona "Exclusive".',
  'strategyParam.suppressRuntimeContext.label': 'Suppress runtime context',
  'strategyParam.suppressRuntimeContext.hint': 'When on, no runtime context is attached',
  'strategyParam.contextName.label': 'Registration name',
  'strategyParam.contextName.hint': 'Section name registered as runtime context; empty uses the config ID',
  'strategyParam.patch.label': 'Request config patch',
  'strategyParam.patch.hint': 'Shallow-merged into the LlmCallConfig of this call (JSON object); an empty object changes nothing',
  'strategyParam.replace.label': 'Replace wholesale',
  'strategyParam.replace.hint': 'When on, the baseline call config is dropped and only the patch above is used',
  'strategyParam.mode.label': 'Stream mode',
  'strategyParam.mode.hint': 'Pass leaves the model stream untouched; replace substitutes this config text for the whole stream',
  'strategyParam.mode.pass': 'Pass through',
  'strategyParam.mode.replace': 'Replace',
  'strategyParam.toolNames.label': 'Target tools',
  'strategyParam.toolNames.hint': 'Comma-separated tool names; empty matches every registered tool, so leave it filled for a targeted gate',
  'strategyParam.preDecision.label': 'Pre-execute decision',
  'strategyParam.preDecision.hint': 'How a matching tool call is decided before execution',
  'strategyParam.preDecision.allow': 'Allow',
  'strategyParam.preDecision.deny': 'Deny',
  'strategyParam.preDecision.ask': 'Ask',
  'strategyParam.denyReason.label': 'Deny reason',
  'strategyParam.denyReason.hint': 'Reason returned to the model on deny; empty uses the engine default',
  'strategyParam.postAction.label': 'Post-execute decision',
  'strategyParam.postAction.hint': 'How a matching tool result is handled',
  'strategyParam.postAction.accept': 'Accept',
  'strategyParam.postAction.replace': 'Replace',
  'strategyParam.postAction.block': 'Block',
  'strategyParam.turnStopNote': 'This layer has no writable instance parameters: the continuation cap is fixed by the engine and not configurable.',
  'strategyParam.subagentEndNote': 'The end event reports an outcome. Choose to record it or inject context into the owning main session; this neither rewrites the child result nor wakes an idle main session.',
  'strategyParam.endAction.label': 'On completion',
  'strategyParam.endAction.hint': 'Injected text is consumed when the main session next processes input. Each child run is delivered once.',
  'strategyParam.endAction.observe': 'Record completion only',
  'strategyParam.endAction.injectMain': 'Inject text into the main session',
  'strategyParam.request.hint': 'This rule changes the model call configuration. Empty fields inherit the original request. Text, tool lists, and message history are outside this interface.',
  'strategyParam.request.provider': 'Provider ID',
  'strategyParam.request.model': 'Model ID',
  'strategyParam.request.reasoningEffort': 'Reasoning effort',
  'strategyParam.request.temperature': 'Temperature',
  'strategyParam.request.maxTokens': 'Maximum output tokens',
  'strategyParam.request.stop': 'Stop sequences',
  'strategyParam.request.stopHint': 'One sequence per line; spaces within a sequence are preserved. Leave empty to inherit.',
  'strategyParam.anchorManaged': 'These fields in the current generated config come from module parameters and are read-only here. Edit the parameters in this layer’s engine settings. Explicit local configs retain higher priority; rebuilding refreshes the actual sources.',
  'strategyParam.managed.local': 'The current generated config uses this config’s local override. Edit the fields below; omitted fields use config defaults.',
  'strategyParam.managed.source': 'Source parameter: {param}',
  'strategyParam.managed.derived': 'computed',
  'strategyParam.managed.empty': 'empty (engine default)',
  'strategyParam.managed.followsCustom': 'model scope follows the custom-guide switch',
  'strategyParam.guideManaged': 'These fields in the current generated config come from module parameters and are read-only here. Edit the parameters in this layer’s engine settings. Explicit local configs retain higher priority; rebuilding refreshes the actual sources.',
  'strategyParam.custom.label': 'Custom',
  'strategyParam.custom.hint': 'When on, always use the custom text; when off, pick by task',
  'strategyParam.anchorText.label': 'Custom anchor text',
  'strategyParam.anchorText.hint': 'Injected as-is when the custom anchor is enabled',
  'strategyParam.buildPattern.label': 'Build task pattern',
  'strategyParam.buildPattern.hint': 'Matching tasks use the build guide',
  'strategyParam.complexPattern.label': 'Complex task pattern',
  'strategyParam.complexPattern.hint': 'Matching tasks use the deep guide',
  'strategyParam.firstTurnBuild': 'Build guide',
  'strategyParam.firstTurnInspect': 'Inspect guide',
  'strategyParam.deepGuide': 'Deep guide',
  'strategyParam.guideText.label': 'Custom guide',
  'strategyParam.guideText.hint': 'Injected as-is when the custom guide is enabled',
  'strategyParam.guideReuse': 'Complex tasks reuse the complex-task pattern of the anchor module.',
  'strategyParam.guideWeak': 'Short guide',
  'strategyParam.firstTurnWord.label': 'Trigger word',
  'strategyParam.firstTurnWord.hint': 'Injected when the first reasoning after promotion contains this word',
  'strategyParam.constant.label': 'Always on',
  'strategyParam.constant.hint': 'When on, inject every turn; when off, only on keyword hits',
  'strategyParam.keys.label': 'Primary keywords',
  'strategyParam.keys.hint': 'Injected when any keyword hits the message',
  'strategyParam.keys.placeholder': 'Type a keyword and press Enter',
  'strategyParam.secondaryKeys.label': 'Secondary keywords',
  'strategyParam.secondaryKeys.hint': 'Combined with the primary keywords for triggering',
  'strategyParam.secondaryKeys.placeholder': 'Type a secondary keyword and press Enter',
  'strategyParam.caseSensitive.label': 'Case sensitive',
  'strategyParam.caseSensitive.hint': 'When on, keywords must match letter case',
  'strategyParam.wholeWords.label': 'Whole words',
  'strategyParam.wholeWords.hint': 'When on, keywords must appear as whole words',
  'strategyParam.useRegex.label': 'Regular expressions',
  'strategyParam.useRegex.hint': 'When on, keywords are matched as regular expressions',
  'strategyParam.selectiveLogic.label': 'Trigger logic',
  'strategyParam.selectiveLogic.hint': 'How primary and secondary keywords combine',
  'strategyParam.fillText.label': 'Custom hint',
  'strategyParam.fillText.hint': 'Overrides files and dynamic probing; empty uses the default',
  'strategyParam.envKeys.label': 'Environment variables',
  'strategyParam.envKeys.hint': 'Environment variables that may be read; comma-separated, empty uses the default',
  'strategyParam.limit.label': 'Entry limit',
  'strategyParam.limit.hint': 'A positive integer; empty means unlimited',
  'strategyParam.fields.label': 'Returned fields',
  'strategyParam.fields.hint': 'Fields returned by the skill catalog; comma-separated',
  'strategyParam.providers.label': 'Source allowlist',
  'strategyParam.providers.hint': 'Skill sources that may be used; comma-separated, empty allows all',
  'strategyParam.emptyBehavior.label': 'Empty result',
  'strategyParam.emptyBehavior.hint': 'Skip, or inject the hint text when nothing is found',
  'strategyParam.emptyText': 'Empty-result hint',
  'strategyParam.noParams': 'This strategy has no extra parameters; template variables are edited above.',

  'variables.title': 'Template variables',
  'variables.cardMeta': 'Module-level {{key}} interpolation · {count} variables',
  'variables.toggleDisable': 'Click to disable template-variable interpolation',
  'variables.toggleEnable': 'Click to enable template-variable interpolation',
  'variables.enableAria': 'Enable template-variable interpolation',
  'variables.confirmClear': 'Confirm clear',
  'variables.cancel': 'Cancel',
  'variables.delete': 'Delete',
  'variables.disabledHint': 'Template-variable interpolation is disabled: {{key}} is no longer replaced (no module-level variable file is generated).',
  'variables.add': 'Add',
  'variables.empty': 'No variables yet; use {{key}} to reference one.',
  'variables.nameAria': 'Template variable name',
  'variables.valueAria': 'Template variable value',
  'variables.namePlaceholder': 'Name',
  'variables.valuePlaceholder': 'Value',
  'variables.removeAria': 'Delete variable {name}',

  'templates.aria': 'Choose a built-in template',
  'templates.title': 'Choose a built-in template ({layer})',
  'templates.closeAria': 'Close the template picker',
  'templates.group.variables': 'Variables',
  'templates.variables.hint': 'Template variables (module-level {{key}} interpolation)',
  'templates.empty': 'No built-in template in this layer.',
  'templates.group.tools': 'Tools',
  'templates.loadFailed': 'Failed to read the template library: {reason}',
  'templates.emptyList': 'Failed to read the template library: the template list is empty',
  'templates.duplicateId': 'The id already exists: {id} (rename or delete the config with the same id first)',
  'templates.inserted': 'Inserted template {file} (id={id})',
  'templates.layer.pre-step': 'Message batch',
  'templates.layer.system-section': 'System section',
  'templates.layer.runtime-context': 'Runtime context',
  'templates.layer.agent-request': 'Agent request',
  'templates.layer.llm-stream': 'Model stream',
  'templates.layer.tool-pipeline': 'Tool pipeline',
  'templates.layer.turn-stop': 'Turn stop',
  'templates.layer.subagent-start': 'Subagent start',
  'templates.layer.subagent-end': 'Subagent end',

  'persona.name': 'Persona',
  'persona.meta.declared': 'Top-level persona in module.yml · same shape as the official @deepseek-ai/dsh-persona row',
  'persona.meta.inherited': 'Not declared: inherits the host deployment persona',
  'persona.prefix.label': 'Prefix',
  'persona.prefix.aria': 'Persona prefix',
  'persona.prefix.hint': 'Rendered as deployment:persona-prefix (before the first-party guidance, official order 0); supports official variables such as {{model}} / {{cwd}}; empty = dropped at render time.',
  'persona.suffix.label': 'Suffix',
  'persona.suffix.aria': 'Persona suffix',
  'persona.suffix.hint': 'Rendered as deployment:persona-suffix (after the first-party guidance, official order 10200); empty = masks the host suffix.',
  'persona.complete.label': 'Exclusive',
  'persona.complete.hint': 'The prefix becomes the only system-prompt section; mutually exclusive with the prompt-config "exclusive" flag',
  'persona.runtimeContext.label': 'Dynamic runtime context',
  'persona.runtimeContext.hint': 'Off = this scope attaches no dynamic snapshot (official includeRuntimeContext:false)',
  'persona.saving': 'Saving…',
  'persona.save': 'Save persona',
  'persona.remove': 'Remove persona section',
  'persona.notice.loadFailed': 'Failed to read the persona',
  'persona.notice.saved': 'Persona saved and rebuilt',
  'persona.notice.removed': 'Persona section removed (falls back to the host deployment persona)',
  'persona.notice.saveFailed': 'Failed to save the persona',
}
